import React, { useState } from 'react';
import { ExternalLink, Phone, Mail, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';

  // Optimiert URL für verschiedene Bildanbieter
const optimizeImageUrl = (url) => {
  if (url.includes('unsplash.com')) {
    return `${url}?w=500&q=80&auto=format&fit=crop`;
  }
  return url;
};

export const BrandCards = ({ locations }) => {
  // Offizielle 2025/2026 Modellbilder von Mazda.de und Nissan.de
  const brandImages = {
    'Nissan': [
      '/brand-images/nissan-qashqai-2026.png',
      '/brand-images/nissan-juke-2026.png',
      '/brand-images/nissan-leaf-2026.jpg'
    ],
    'Mazda': [
      '/brand-images/mazda-cx5-2026.jpg',
      '/brand-images/mazda-cx30.jpg',
      '/brand-images/mazda-cx60-2026.jpg'
    ],
    'Omoda & Jaecoo': [
      '/brand-images/jaecoo-7.jpg'
    ]
  };

  return (
    <section id="marken" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Unsere Standorte
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Besuchen Sie uns an einem unserer Standorte in Magdeburg
          </p>
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
          {locations.map((location, index) => (
            <Card
              key={location.id}
              className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 border-0 flex flex-col"
              style={{
                animationDelay: `${index * 150}ms`,
              }}
            >
              {/* Brand Logo */}
              <div className="bg-white p-6 flex justify-center border-b h-[104px] items-center">
                <img
                  src={location.logo}
                  alt={`${location.brand} logo`}
                  className={location.brand === 'Omoda & Jaecoo' ? 'h-8 w-auto max-w-[180px] object-contain' : 'h-16 w-auto object-contain'}
                />
              </div>

              {/* Vehicle Image */}
              <div className="relative h-48 overflow-hidden bg-gray-100">
                <img
                  src={optimizeImageUrl(brandImages[location.brand]?.[index % (brandImages[location.brand]?.length || 1)])}
                  alt={`${location.brand} Fahrzeug`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none"></div>
              </div>

              {/* Content */}
              <div className="p-8 bg-white flex-grow">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  {location.brand} Standort
                </h3>
                
                <div className="space-y-4 mb-6">
                  {/* Address */}
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-gray-700" />
                    </div>
                    <div>
                      <p className="text-gray-900 font-semibold">
                        {location.address.street}
                      </p>
                      <p className="text-gray-600">
                        {location.address.zip} {location.address.city}
                      </p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center">
                      <Phone className="w-5 h-5 text-gray-700" />
                    </div>
                    <a
                      href={`tel:${location.phone.replace(/\s/g, '')}`}
                      className="text-gray-900 font-semibold hover:text-red-600 transition-colors"
                    >
                      {location.phone}
                    </a>
                  </div>

                  {/* Email */}
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center">
                      <Mail className="w-5 h-5 text-gray-700" />
                    </div>
                    <a
                      href={`mailto:${location.email}`}
                      className="text-gray-900 font-semibold hover:text-red-600 transition-colors"
                    >
                      E-Mail senden
                    </a>
                  </div>
                </div>

                <a
                  href={location.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block w-full"
                >
                  <Button
                    className="w-full bg-gray-900 hover:bg-gray-800 text-white px-6 py-4 text-base font-semibold rounded-lg transition-all duration-300 hover:scale-105 shadow-lg group"
                  >
                    <span className="flex items-center justify-center gap-2">
                      Zur Händlerseite
                      <ExternalLink className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </span>
                  </Button>
                </a>
              </div>

              {/* Accent Border */}
              <div
                className="absolute top-0 left-0 right-0 h-1 transition-all duration-500 group-hover:h-2"
                style={{ backgroundColor: location.color }}
              ></div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
